
import { HttpClient } from '@angular/common/http';
import { Component, effect, inject, OnDestroy, OnInit, signal } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { ActivatedRoute } from '@angular/router';
import { combineLatest, distinctUntilChanged, fromEvent, map, startWith, Subject, takeUntil } from 'rxjs';
import environment from '../../environments/environment';
import { TechsStore } from '../core/stores/techs.store';
import { InboxComponent } from '../inbox/inbox.component';
import { ArrayApiResponse, ButtonInfo, Ecosystem, Tag, TechDetail } from '../models/models';
import { PaginationButtonsComponent } from '../pagination-buttons/pagination-buttons.component';
import { SearchParamService } from '../services/searchParam.service';
import { TechService } from '../services/tech.service';
import { TagSelectorComponent } from '../tag-selector/tag-selector.component';
import { OverviewCardComponent } from '../tech-overview-card/overview-card.component';


@Component({
  standalone: true,
  imports: [OverviewCardComponent, PaginationButtonsComponent, MatProgressSpinnerModule, FormsModule, TagSelectorComponent, InboxComponent],
  selector: 'app-tech-overview',
  templateUrl: './tech-overview.component.html',
  styleUrls: ['./tech-overview.component.css'],
})
export class TechOverviewComponent implements OnInit, OnDestroy {
  private techService = inject(TechService);
  private searchParamService = inject(SearchParamService);
  private route = inject(ActivatedRoute);
  private http = inject(HttpClient);

  readonly techsStore = inject(TechsStore);

  ALL_VALUES: string = 'ALL';

  activeTechs: TechDetail[] = [];
  ecosystems: Ecosystem[] = [];
  tags: Tag[] = [];

  search = signal<string>('')

  groupedButtonInfo: Record<string, ButtonInfo> = {};
  selectedEcosystem: string = this.ALL_VALUES;
  selectedTag: string = this.ALL_VALUES;

  isMobileView: boolean = false;
  showAllTechs: boolean = false;

  page: number = 1;
  pageSize: number = 30;
  paginationName = 'page';

  private ngUnsubscribe = new Subject<void>();

  constructor() {
    //keep store's search filter up to date with two-way bound search signal
    effect(() => this.techsStore.updateSearch(this.search()), {
      allowSignalWrites: true
    })
  }

  ngOnInit(): void {
    this.getCollabResourceButton();

    this.search.set(this.techsStore.search())

    fromEvent(window, 'resize')
      .pipe(
        map(() => window.innerWidth),
        startWith(window.innerWidth),
        distinctUntilChanged(),
        takeUntil(this.ngUnsubscribe)
      )
      .subscribe((width) => {
        this.isMobileView = width <= 768;
      });

    combineLatest([this.techService.getEcosystems(), this.techService.getTags()])
      .pipe(
        map(([ecosystemResponse, tagResponse]) =>
        ({
          ecosystems: mapResponse(ecosystemResponse),
          tags: mapResponse(tagResponse),
        })),
        takeUntil(this.ngUnsubscribe)
      )
      .subscribe(({ ecosystems, tags }) => {
        this.ecosystems = ecosystems;
        this.tags = tags;

        this.selectedEcosystem = window.sessionStorage.getItem("ecoId") ?? this.ALL_VALUES;
        this.selectedTag = window.sessionStorage.getItem("tagId") ?? this.ALL_VALUES;

      });

    this.searchParamService.getNumberedSearchParam(this.paginationName, 1)
      .pipe(takeUntil(this.ngUnsubscribe))
      .subscribe(pageNumber => this.page = pageNumber)
  }

  ngOnDestroy(): void {
    this.ngUnsubscribe.next();
    this.ngUnsubscribe.complete();
  }

  setEcosystemFilter(ecosystemId: string) {
    window.sessionStorage.setItem("ecoId", ecosystemId);
    this.selectedEcosystem = ecosystemId;
  }

  setTagFilter(tagId: string) {
    window.sessionStorage.setItem("tagId", tagId);
    this.selectedTag = tagId;
  }

  get filteredTechs() {
    return this.techsStore.getFilteredTechs()
  }

  get pagedTechs() {
    return this.techsStore.getPagedTechs(this.page, this.pageSize)
  }

  tagsChanged(newTags: Tag[]) {
    this.techsStore.updateSelectedTags(newTags.map(t => t.id || ''))
  }
  ecosystemsChanged(newTags: Ecosystem[]) {
    this.techsStore.updateSelectedEcosystems(newTags.map(t => t.id || ''))
  }

  async getCollabResourceButton() {
    const url = `${environment.collabBackendApiUrl}/api/resource/allButtoninfo`;
    const params = { url: 'tech' };
    const headers = {
      Authorization: `Bearer ${window.sessionStorage.getItem('JWT')}`
    };

    const observer = {
      next: (response: ButtonInfo[]) => {
        const grouped = response.reduce((prev, next) => {
          const id = next.resource?.split('/')[next.resource.split('/').length - 1] || '';
          return { ...prev, [id]: next };
        }, {} as Record<string, ButtonInfo>);
        this.groupedButtonInfo = grouped;
      },
      error: (error: any) => {
        console.error(error);
      }
    };

    this.http.get<Array<ButtonInfo>>(url, { params, headers }).subscribe(observer);
  }
}

const mapResponse = <TResponse>(response: ArrayApiResponse<TResponse>): TResponse[] => !response.error ? (response.data ? response.data : []) : [];
